var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useMemo } from "react";
import { selectors, useAppSelector } from "store";
import { totalTime } from "ui/components/elements/chart/utility";
import { PAD_SECONDS } from "ui/components/panels/graphs/constants";
/**
 * Converts a play location fraction (0–1 of the padded application-speed
 * timeline) to a Recharts x-axis time in seconds, where t=0 is the pick moment
 * (start of the API data) and the padded start sits at t=-PAD_SECONDS.
 *
 * This is the single source of truth for mapping the shared
 * `playLocationPercent` onto the graph time axis. Both the graph PlayLine and
 * the current vacuum-level / air-consumption lookups must use it so the
 * indicators stay aligned with the rendered play head. Deriving the offset from
 * the timeline (wizard before-pick values) rather than the API's first
 * timestamp avoids a sub-frame drift between the two — see the ATM indicator
 * firing early bug.
 */
export var percentToDisplayTime = function (percent, timeline) {
    return percent * timeline.totalTime - timeline.blowOffOnBeforePick.end;
};
/** Inverse of {@link percentToDisplayTime}. */
export var displayTimeToPercent = function (time, timeline) {
    return (time + timeline.blowOffOnBeforePick.end) / timeline.totalTime;
};
/**
 * All distinct interval-boundary times in the timeline (in seconds), sorted
 * ascending. These are the "key points" of the cycle — pick, PS (part
 * secured), energy-save start/end, ATM/release, place, return — and are used
 * as snap targets when the user drags the play head so it can land exactly on
 * an event rather than a pixel near it. Optional intervals (energy saving,
 * additional release) are skipped when absent.
 */
export var getTimelineBoundaryTimes = function (timeline) {
    var e_1, _a;
    var intervals = [
        timeline.vacuumOnBeforePick,
        timeline.blowOffOnBeforePick,
        timeline.timeToPartSecured,
        timeline.delayToEnergySave,
        timeline.timeWithEnergySavingOn,
        timeline.timeWithEnergySavingOff,
        timeline.transfer,
        timeline.releaseTimeToPlacePart,
        timeline.additionalReleaseTime,
        timeline.returnTime,
    ];
    var times = new Set();
    try {
        for (var intervals_1 = __values(intervals), intervals_1_1 = intervals_1.next(); !intervals_1_1.done; intervals_1_1 = intervals_1.next()) {
            var interval = intervals_1_1.value;
            if (!interval)
                continue;
            times.add(interval.begin);
            times.add(interval.end);
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (intervals_1_1 && !intervals_1_1.done && (_a = intervals_1.return)) _a.call(intervals_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return __spreadArray([], __read(times), false).sort(function (a, b) { return a - b; });
};
export var useChartTimeline = function () {
    var dashboardData = useAppSelector(selectors.selectDashboardData);
    var timeline = useMemo(function () {
        if (!dashboardData)
            return;
        var speedData = dashboardData.applicationSpeed;
        var desiredVacuumLevels = dashboardData.desiredVacuumLevels;
        var totalDuration = totalTime(speedData);
        if (totalDuration === 0)
            return;
        var vacuumOnBeforePick = {
            begin: PAD_SECONDS,
            end: PAD_SECONDS + speedData.vacuumOnBeforePick.value,
        };
        var blowOffOnBeforePick = {
            begin: vacuumOnBeforePick.end,
            end: vacuumOnBeforePick.end + speedData.blowOffOnBeforePick.value,
        };
        var timeToPartSecured = {
            begin: blowOffOnBeforePick.end,
            end: blowOffOnBeforePick.end + speedData.timeToPartSecured.value,
        };
        var delayToEnergySave, timeWithEnergySavingOn = undefined;
        var timeWithEnergySavingOff;
        // Energy saving is only active when all three fields are defined:
        // a vacuum level target, a delay before activating, and a duration while on.
        // If only timeWithEnergySavingOff exists, the feature is disabled.
        if (speedData.delayToEnergySave &&
            desiredVacuumLevels.energySaving &&
            speedData.timeWithEnergySavingOn) {
            delayToEnergySave = {
                begin: timeToPartSecured.end,
                end: timeToPartSecured.end + speedData.delayToEnergySave.value,
            };
            timeWithEnergySavingOn = {
                begin: delayToEnergySave.end,
                end: delayToEnergySave.end + speedData.timeWithEnergySavingOn.value,
            };
        }
        else if (speedData.timeWithEnergySavingOff) {
            timeWithEnergySavingOff = {
                begin: timeToPartSecured.end,
                end: timeToPartSecured.end + speedData.timeWithEnergySavingOff.value,
            };
        }
        // transfer.end (= start of release) lands on the place moment because
        // transferToPlace / the energy-saving durations are derived from the
        // sampled motion curve (place_start − part_secured) in buildDashboardData.
        var transfer = {
            begin: timeToPartSecured.end,
            end: timeWithEnergySavingOn
                ? timeWithEnergySavingOn.end
                : timeWithEnergySavingOff
                    ? timeWithEnergySavingOff.end
                    : timeToPartSecured.end,
        };
        var releaseTimeToPlacePart = {
            begin: transfer.end,
            end: transfer.end + speedData.releaseTimeToPlacePart.value,
        };
        var additionalReleaseTime = {
            begin: releaseTimeToPlacePart.end,
            end: releaseTimeToPlacePart.end + speedData.additionalReleaseTime.value,
        };
        var returnTime = {
            begin: releaseTimeToPlacePart.end,
            end: releaseTimeToPlacePart.end + speedData.returnToPick.value,
        };
        var timeline = {
            vacuumOnBeforePick: vacuumOnBeforePick,
            blowOffOnBeforePick: blowOffOnBeforePick,
            timeToPartSecured: timeToPartSecured,
            delayToEnergySave: delayToEnergySave,
            timeWithEnergySavingOn: timeWithEnergySavingOn,
            timeWithEnergySavingOff: timeWithEnergySavingOff,
            transfer: transfer,
            releaseTimeToPlacePart: releaseTimeToPlacePart,
            additionalReleaseTime: additionalReleaseTime,
            returnTime: returnTime,
            totalTime: totalDuration + PAD_SECONDS,
        };
        return timeline;
    }, [dashboardData]);
    return timeline;
};
